import api, { BACKEND_URL } from "@/lib/api";

/**
 * DriveDesk AI — reusable streaming-chat client.
 *
 * Deliberately built with the raw `fetch` Streams API (not axios) since
 * browsers don't support streaming response bodies through XHR-based
 * clients. Kept in its own module — alongside `useAIAssistant.js` — so
 * this whole surface can be lifted into a future 815 Digital Services
 * app with minimal changes: only the backend base URL/auth mechanism
 * would need to change.
 *
 * v1.3 Wave 2: the stream may also emit an `event: action_plan` frame
 * (a fully-formed, already-persisted, status="proposed" plan) — handled
 * via the optional `onActionPlan` callback, kept separate from `onDelta`
 * since a plan is structured data, not more chat text.
 */
export async function streamAIChat(message, { onDelta, onActionPlan, onDone, onError, signal }) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/ai/chat/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
      signal,
    });

    if (!res.ok || !res.body) {
      const detail = await res.json().catch(() => null);
      throw new Error(detail?.detail || `Request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let doneMeta = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const evt of events) {
        if (!evt.trim()) continue;
        const lines = evt.split("\n");
        let eventType = "message";
        let dataLine = "";
        for (const line of lines) {
          if (line.startsWith("event:")) eventType = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLine = line.slice(5).trim();
        }
        if (!dataLine) continue;
        let parsed;
        try {
          parsed = JSON.parse(dataLine);
        } catch {
          continue;
        }
        if (eventType === "done") {
          doneMeta = parsed;
        } else if (eventType === "action_plan") {
          if (parsed.plan) onActionPlan?.(parsed.plan);
        } else if (parsed.delta) {
          onDelta(parsed.delta);
        }
      }
    }
    onDone(doneMeta || {});
  } catch (err) {
    if (err?.name === "AbortError") return;
    onError(err);
  }
}

/**
 * DriveDesk AI Wave 2 — Action Plan approval endpoints. These are the
 * ONLY two calls in the entire frontend that can ever cause an Action
 * Plan to mutate real data (`approvePlan`) or be taken off the table
 * (`rejectPlan`) — both require an explicit user click, never called
 * automatically.
 */
export async function approvePlan(planId) {
  const res = await api.post(`/ai/actions/plans/${planId}/approve`);
  return res.data;
}

export async function rejectPlan(planId) {
  const res = await api.post(`/ai/actions/plans/${planId}/reject`);
  return res.data;
}
